import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, XCircle, Clock, Sunrise, Sunset, Minus, Plus } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail, money } from "@/lib/api";

export default function AddonAccept() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeLate, setIncludeLate] = useState(true);
  const [extendCount, setExtendCount] = useState(0);

  useEffect(() => {
    api.get(`/addon-offers/${token}`)
      .then((r) => { setOffer(r.data); setIncludeLate(!!r.data.include_late_checkout); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const currency = offer?.currency || "usd";
  const unavailable = offer && ["paid", "expired", "cancelled"].includes(offer.status);
  const isEarly = offer?.kind === "early_checkin";
  const opts = offer?.extend_options || [];
  const extendTotal = opts.slice(0, extendCount).reduce((s, o) => s + Number(o.price), 0);
  const total = isEarly
    ? Number(offer?.fee || 0)
    : (includeLate ? Number(offer?.late_fee || 0) : 0) + extendTotal;

  const pay = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/addon-offers/${token}/pay`, {
        origin_url: window.location.origin,
        late_checkout: isEarly ? false : includeLate,
        extend_nights: isEarly ? 0 : extendCount,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10" data-testid="addon-accept-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {loading && <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>}

        {!loading && error && !offer && (
          <div className="text-center" data-testid="addon-error">
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && offer && (
          <>
            <div className="text-center mb-6">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mb-3">
                {isEarly ? <Sunrise className="h-6 w-6 text-primary" /> : <Sunset className="h-6 w-6 text-primary" />}
              </span>
              <h1 className="font-serif text-2xl font-semibold text-foreground">Hi {offer.guest_name}!</h1>
              <p className="text-sm text-muted-foreground mt-1">{isEarly ? "Would you like to check in early?" : "Make the most of your last day"}</p>
            </div>

            {unavailable ? (
              <div className="text-center py-4" data-testid="addon-unavailable">
                <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="font-medium text-foreground capitalize">This offer is {offer.status}.</p>
                <p className="text-sm text-muted-foreground mt-1">Please reach out if you'd still like to arrange this.</p>
              </div>
            ) : isEarly ? (
              <div className="rounded-xl bg-accent/50 p-4 mb-5" data-testid="addon-early-block">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium">Early check-in from {offer.checkin_time}</span>
                  <span className="text-primary font-serif text-2xl font-semibold" data-testid="addon-early-fee">{money(offer.fee, currency)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Skip the wait and settle in sooner.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {offer.include_late_checkout && (
                  <button type="button" data-testid="addon-late-toggle" onClick={() => setIncludeLate((v) => !v)}
                    className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-all ${includeLate ? "border-primary bg-primary/5" : "border-border hover:border-secondary"}`}>
                    <span className={`h-4 w-4 rounded flex-shrink-0 flex items-center justify-center border ${includeLate ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>{includeLate && <Check className="h-3 w-3 text-white" />}</span>
                    <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-foreground">Late checkout until {offer.checkout_time}</span><span className="block text-xs text-muted-foreground">Relax a little longer before you head out.</span></span>
                    <span className="text-sm font-semibold text-foreground">{money(offer.late_fee, currency)}</span>
                  </button>
                )}

                {opts.length > 0 && (
                  <div className="rounded-lg border border-border px-3 py-3" data-testid="addon-extend-block">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">Extend your stay</span>
                        <span className="block text-xs text-muted-foreground">Up to {opts.length} more night(s) available.</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid="addon-extend-minus" onClick={() => setExtendCount((n) => Math.max(0, n - 1))}><Minus className="h-4 w-4" /></Button>
                        <span className="w-6 text-center font-medium" data-testid="addon-extend-count">{extendCount}</span>
                        <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-full" data-testid="addon-extend-plus" onClick={() => setExtendCount((n) => Math.min(opts.length, n + 1))}><Plus className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    {extendCount > 0 && (
                      <div className="mt-3 space-y-1 border-t border-border pt-2">
                        {opts.slice(0, extendCount).map((o) => (
                          <div key={o.date} className="flex justify-between text-xs text-muted-foreground"><span>Night of {o.date}</span><span className="text-foreground">{money(o.price, currency)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!unavailable && (
              <>
                <div className="flex justify-between items-center border-t border-border pt-3 mb-4">
                  <span className="text-foreground font-medium">Total due now</span>
                  <span className="text-primary font-serif text-2xl font-semibold" data-testid="addon-total">{money(total, currency)}</span>
                </div>
                {error && <p className="text-sm text-destructive text-center mb-3" data-testid="addon-pay-error">{error}</p>}
                <Button className="w-full rounded-full bg-primary h-12 text-base" disabled={busy || total <= 0} data-testid="addon-pay-btn" onClick={pay}>
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5 mr-1" /> Pay {money(total, currency)}</>}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Secure checkout powered by Stripe</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
